import { PluginKey } from "@tiptap/pm/state";
import type { SuggestionKeyDownProps, SuggestionProps } from "@tiptap/suggestion";
import { createRoot, type Root } from "react-dom/client";
import type { ReferencePresentationResolver } from "../mentions";
import { type MentionItem, MentionList } from "./mention-list";
import { resolveMentionPopupPosition } from "./popup-positioning";

interface MentionSuggestionOptions {
  searchItems: (query: string) => Promise<MentionItem[]>;
  resolvePresentation?: ReferencePresentationResolver;
}

export const messageMentionSuggestionPluginKey = new PluginKey("messageMentionSuggestion");

/**
 * Creates the suggestion config for the Mention extension.
 * Bridges Tiptap's imperative suggestion lifecycle into React rendering.
 */
export function createMentionSuggestion({
  searchItems,
  resolvePresentation,
}: MentionSuggestionOptions) {
  return {
    pluginKey: messageMentionSuggestionPluginKey,
    items: async ({ query }: { query: string }) => {
      try {
        return await searchItems(query);
      } catch {
        return [];
      }
    },

    render: () => {
      let popup: HTMLDivElement | null = null;
      let root: Root | null = null;
      let ref: { onKeyDown: (props: { event: KeyboardEvent }) => boolean } | null = null;
      let latestProps: SuggestionProps<MentionItem> | null = null;
      let rafId: number | null = null;
      let removeViewportListeners: (() => void) | null = null;

      const cancelScheduledPosition = () => {
        if (rafId === null) return;
        window.cancelAnimationFrame(rafId);
        rafId = null;
      };

      const applyPosition = (target: HTMLDivElement, props: SuggestionProps<MentionItem>) => {
        const rect = props.clientRect?.();
        if (!rect) return;

        const popupRect = target.getBoundingClientRect();
        const popupWidth = popupRect.width || target.offsetWidth || 240;
        const popupHeight = popupRect.height || target.offsetHeight || 200;

        const position = resolveMentionPopupPosition({
          anchorRect: rect,
          popupWidth,
          popupHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        });

        target.style.left = `${position.left}px`;
        target.style.top = `${position.top}px`;
        target.dataset.placement = position.placement;
        if (position.maxHeight !== undefined) {
          target.style.maxHeight = `${position.maxHeight}px`;
          target.style.setProperty("--mention-popup-max-height", `${position.maxHeight}px`);
        } else {
          target.style.removeProperty("max-height");
          target.style.removeProperty("--mention-popup-max-height");
        }
      };

      const schedulePosition = (props?: SuggestionProps<MentionItem>) => {
        if (props) {
          latestProps = props;
        }

        if (!popup || !latestProps) return;
        cancelScheduledPosition();
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          if (!popup || !latestProps) return;
          applyPosition(popup, latestProps);
        });
      };

      return {
        onStart: (props: SuggestionProps<MentionItem>) => {
          latestProps = props;
          popup = document.createElement("div");
          popup.style.position = "fixed";
          popup.style.zIndex = "1000";
          popup.style.overflowY = "auto";

          root = createRoot(popup);
          document.body.appendChild(popup);
          root.render(
            <MentionList
              items={props.items}
              command={props.command}
              resolvePresentation={resolvePresentation}
              ref={(instance) => {
                ref = instance;
              }}
            />
          );

          schedulePosition(props);
          const handleViewportChange = () => {
            schedulePosition();
          };
          window.addEventListener("resize", handleViewportChange);
          window.addEventListener("scroll", handleViewportChange, true);
          removeViewportListeners = () => {
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
            removeViewportListeners = null;
          };
        },

        onUpdate: (props: SuggestionProps<MentionItem>) => {
          latestProps = props;
          root?.render(
            <MentionList
              items={props.items}
              command={props.command}
              resolvePresentation={resolvePresentation}
              ref={(instance) => {
                ref = instance;
              }}
            />
          );

          schedulePosition(props);
        },

        onKeyDown: (props: SuggestionKeyDownProps) => {
          if (props.event.key === "Escape") {
            popup?.remove();
            return true;
          }
          return ref?.onKeyDown({ event: props.event }) ?? false;
        },

        onExit: () => {
          cancelScheduledPosition();
          removeViewportListeners?.();

          // Defer unmount so React can finish current render cycle.
          const currentRoot = root;
          const currentPopup = popup;
          setTimeout(() => {
            currentRoot?.unmount();
            currentPopup?.remove();
          }, 0);
          popup = null;
          root = null;
          ref = null;
          latestProps = null;
        },
      };
    },
  };
}
